import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Settings2 } from "lucide-react";
import { useConsent } from "@/contexts/ConsentContext";

export function CookieConsentBanner() {
    const { consent, acceptAll, rejectAll, setConsent, preferencesOpen, openPreferences, closePreferences } = useConsent();
    const [localPrefs, setLocalPrefs] = useState({
        analytics: !!consent.analytics,
        advertising: !!consent.advertising,
    });

    // Sync the modal's draft state with global consent whenever modal opens or
    // the global consent value changes externally.
    useEffect(() => {
        if (preferencesOpen) {
            setLocalPrefs({ analytics: !!consent.analytics, advertising: !!consent.advertising });
        }
    }, [preferencesOpen, consent.analytics, consent.advertising]);

    const bannerVisible = !consent.decided && !preferencesOpen;

    if (!bannerVisible && !preferencesOpen) return null;

    return (
        <>
            {bannerVisible && (
                <div
                    role="region"
                    aria-label="Cookie consent"
                    className="fixed bottom-0 inset-x-0 z-[60] border-t backdrop-blur-xl"
                    style={{
                        background: "rgba(10,13,17,0.96)",
                        borderColor: "var(--ok-border)",
                        color: "var(--ok-text)",
                    }}
                    data-testid="cookie-banner"
                >
                    <div className="ok-container py-4 sm:py-5 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            <Cookie className="w-5 h-5 text-[var(--ok-accent)] mt-0.5 flex-shrink-0" aria-hidden="true" />
                            <div className="min-w-0">
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ok-accent)] mb-1">Cookie & tracking notice</div>
                                <p className="text-xs sm:text-sm leading-relaxed opacity-85">
                                    We use essential cookies to run the archive. With your consent we also use analytics (Google Analytics 4)
                                    and advertising measurement (Meta Pixel, TikTok Pixel) to improve the site and measure campaigns.
                                    {" "}
                                    <Link to="/cookie-policy" className="underline hover:text-[var(--ok-accent)]" data-testid="cookie-banner-policy-link">
                                        Cookie policy
                                    </Link>
                                    {" · "}
                                    <Link to="/privacy" className="underline hover:text-[var(--ok-accent)]" data-testid="cookie-banner-privacy-link">
                                        Privacy
                                    </Link>
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 lg:flex-nowrap lg:flex-shrink-0">
                            <button
                                onClick={rejectAll}
                                className="ok-btn-outline text-xs sm:text-sm"
                                data-testid="cookie-banner-reject"
                            >
                                Reject non-essential
                            </button>
                            <button
                                onClick={openPreferences}
                                className="ok-btn-outline text-xs sm:text-sm inline-flex items-center gap-1.5"
                                data-testid="cookie-banner-manage"
                            >
                                <Settings2 className="w-3.5 h-3.5" /> Manage preferences
                            </button>
                            <button
                                onClick={acceptAll}
                                className="ok-btn-primary text-xs sm:text-sm"
                                data-testid="cookie-banner-accept"
                            >
                                Accept all
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {preferencesOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="consent-prefs-title"
                    className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4"
                    data-testid="consent-prefs-modal"
                >
                    <div
                        className="absolute inset-0"
                        style={{ background: "rgba(0,0,0,0.65)" }}
                        onClick={closePreferences}
                        aria-hidden="true"
                    />
                    <div
                        className="relative ok-card max-w-lg w-full sm:rounded-sm overflow-hidden"
                        style={{ background: "var(--ok-bg)" }}
                    >
                        <div className="flex items-start justify-between p-5 border-b" style={{ borderColor: "var(--ok-border)" }}>
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ok-accent)]">Privacy controls</div>
                                <h2 id="consent-prefs-title" className="font-display text-xl mt-1">Manage tracking preferences</h2>
                            </div>
                            <button
                                onClick={closePreferences}
                                className="p-1.5 rounded-sm hover:bg-[var(--ok-surface)]"
                                aria-label="Close preferences"
                                data-testid="consent-prefs-close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="p-5 space-y-4 text-sm">
                            <PrefRow
                                title="Essential"
                                description="Required for login, security, and basic site functionality. Always on."
                                checked
                                disabled
                                testId="consent-pref-essential"
                            />
                            <PrefRow
                                title="Analytics (Google Analytics 4)"
                                description="Helps us understand which pages and features are used. Aggregated, no encounter descriptions or personal account data."
                                checked={localPrefs.analytics}
                                onChange={(v) => setLocalPrefs((p) => ({ ...p, analytics: v }))}
                                testId="consent-pref-analytics"
                            />
                            <PrefRow
                                title="Advertising measurement (Meta Pixel, TikTok Pixel)"
                                description="Measures the performance of ad campaigns that bring witnesses to the archive. Required for retargeting and conversion reporting."
                                checked={localPrefs.advertising}
                                onChange={(v) => setLocalPrefs((p) => ({ ...p, advertising: v }))}
                                testId="consent-pref-advertising"
                            />
                            <p className="text-xs opacity-60 leading-relaxed pt-2">
                                You can change these choices any time using the "Manage cookies" link in the footer.
                                See our{" "}
                                <Link to="/cookie-policy" className="underline hover:text-[var(--ok-accent)]">cookie policy</Link>
                                {" "}and{" "}
                                <Link to="/privacy" className="underline hover:text-[var(--ok-accent)]">privacy policy</Link>
                                {" "}for full details.
                            </p>
                        </div>

                        <div className="flex flex-wrap gap-2 p-5 border-t" style={{ borderColor: "var(--ok-border)" }}>
                            <button
                                onClick={() => { rejectAll(); closePreferences(); }}
                                className="ok-btn-outline text-xs sm:text-sm"
                                data-testid="consent-prefs-reject"
                            >
                                Reject non-essential
                            </button>
                            <button
                                onClick={() => { acceptAll(); closePreferences(); }}
                                className="ok-btn-outline text-xs sm:text-sm"
                                data-testid="consent-prefs-accept-all"
                            >
                                Accept all
                            </button>
                            <button
                                onClick={() => { setConsent(localPrefs); closePreferences(); }}
                                className="ok-btn-primary text-xs sm:text-sm ml-auto"
                                data-testid="consent-prefs-save"
                            >
                                Save preferences
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function PrefRow({ title, description, checked, onChange, disabled, testId }) {
    return (
        <label
            className="flex items-start gap-3 p-3 ok-card cursor-pointer"
            style={{
                background: "var(--ok-glass-bg)",
                opacity: disabled ? 0.7 : 1,
                cursor: disabled ? "default" : "pointer",
            }}
            data-testid={testId}
        >
            <input
                type="checkbox"
                checked={!!checked}
                disabled={!!disabled}
                onChange={(e) => onChange && onChange(e.target.checked)}
                className="mt-1 accent-[var(--ok-accent)]"
                data-testid={`${testId}-toggle`}
            />
            <div className="flex-1 min-w-0">
                <div className="font-medium">{title}</div>
                <div className="text-xs opacity-70 mt-1 leading-relaxed">{description}</div>
            </div>
        </label>
    );
}
